import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import type { PurchaseFrequency } from "../types/onboarding-state";

export type { PurchaseFrequency };

/**
 * Public contract for the StepFour purchase-frequency selector.
 *
 * The component is fully controlled by the parent page:
 *   - `value` is the current selection, or `""` when nothing is selected
 *     yet. The page initializes it from `onboardingData.purchase_frequency`
 *     so a remount resumes with the persisted choice.
 *   - `onValueChange` is called with a typed `PurchaseFrequency` whenever
 *     the user picks a different option. The page updates its draft and
 *     clears any stale validation error in the same callback.
 *   - `error` is rendered as an inline `role="alert"` block under the
 *     radio group when the page rejected an empty-selection Siguiente.
 */
export interface StepFourProps {
  /** Current selected frequency; empty string when nothing is selected. */
  value: PurchaseFrequency | "";
  /** Called whenever the user picks a different frequency. */
  onValueChange: (value: PurchaseFrequency) => void;
  /** Optional inline error message displayed beneath the radio group. */
  error?: string;
}

/**
 * Canonical ordering and labels for the purchase-frequency options.
 *
 * Keys are the typed `PurchaseFrequency` values persisted to localStorage;
 * labels are the Spanish copy shown to the user. Splitting the two keeps
 * the storage shape stable even when the UI copy is re-translated.
 */
const PURCHASE_FREQUENCY_OPTIONS: ReadonlyArray<{
  value: PurchaseFrequency;
  label: string;
}> = [
  { value: "daily", label: "Diario" },
  { value: "weekly", label: "Semanal" },
  { value: "monthly", label: "Mensual" },
  { value: "other", label: "Semanal/Mensual" },
];

/**
 * Step 4 of the onboarding wizard — purchase-frequency radio question.
 *
 * Renders a controlled Radix `RadioGroup` with the four canonical options
 * and an optional inline error block. The component never owns the submit
 * action; the parent page owns the draft state and triggers persistence
 * on Siguiente.
 */
const StepFour = ({ value, onValueChange, error }: StepFourProps) => {
  return (
    <section className="flex flex-col gap-4" aria-label="Paso 4">
      <h1 className="text-2xl font-semibold leading-tight text-foreground">
        ¿Con qué frecuencia haces tus compras?
      </h1>
      <p className="text-muted-foreground leading-relaxed">
        Selecciona la opción que mejor describe tu rutina para que podamos
        sugerirte productos y ahorros alineados con ella.
      </p>
      <RadioGroup
        value={value}
        onValueChange={(next) => onValueChange(next as PurchaseFrequency)}
        aria-label="Frecuencia de compras"
        data-testid="step-four-radio-group"
      >
        {PURCHASE_FREQUENCY_OPTIONS.map((option) => {
          const itemId = `step-four-${option.value}`;
          return (
            <div key={option.value} className="flex items-center gap-2">
              <RadioGroupItem value={option.value} id={itemId} />
              <Label htmlFor={itemId} className="cursor-pointer">
                {option.label}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
      {error ? (
        <p
          role="alert"
          className="text-sm text-destructive"
          data-testid="step-four-error"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
};

export default StepFour;
